/* ══════════════════════════════════════════════════════════════════════
   SHARED FORMATTERS
   One source of truth for dates, numbers, percentages, labels.
   All timestamps treated as UTC from backend.
   ══════════════════════════════════════════════════════════════════════ */

/** Format number with locale, null → "—" */
export function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

/** Format percentage from a 0-100 rate, null → "—" */
export function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n == null) return '—';
  return n.toFixed(decimals) + '%';
}

/** Format percentage from a 0-1 ratio, null → "n/a" */
export function fmtRatio(n: number | null | undefined, decimals = 1): string {
  if (n == null) return 'n/a';
  return (n * 100).toFixed(decimals) + '%';
}

/** Parse backend naive UTC timestamp safely ("2026-03-22 23:29:43" → Date) */
export function parseTS(ts: string | null | undefined): Date | null {
  if (!ts) return null;
  const normalized = ts.includes('T') || ts.endsWith('Z') ? ts : ts.replace(' ', 'T') + 'Z';
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

/** Human-readable "X ago" from backend timestamp string */
export function timeAgo(ts: string | null | undefined): string {
  const d = parseTS(ts);
  if (!d) return 'never';
  const ms = Date.now() - d.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Gap display: positive → "−X" (deficit), zero/negative → "✓" */
export function fmtGap(n: number | null | undefined): string {
  if (n == null) return '—';
  return n > 0 ? `−${n}` : '✓';
}

/** Confidence → chip variant */
export function confVariant(c: string | null | undefined) {
  if (c === 'high') return 'success' as const;
  if (c === 'moderate') return 'info' as const;
  if (c === 'low') return 'warning' as const;
  return 'muted' as const;
}

/** Reply rate → CSS color */
export function replyColor(r: number | null): string {
  if (r == null) return 'var(--text-ghost)';
  if (r >= 2) return 'var(--green)';
  if (r >= 1) return 'var(--yellow)';
  return 'var(--red)';
}

/** Alert severity → chip variant (backend uses red/amber/info) */
export function sevVariant(s: string) {
  if (s === 'red') return 'error' as const;
  if (s === 'amber') return 'warning' as const;
  if (s === 'info') return 'info' as const;
  return 'muted' as const;
}

/** Alert severity → CSS color */
export function sevColor(s: string): string {
  if (s === 'red') return 'var(--red)';
  if (s === 'amber') return 'var(--yellow)';
  if (s === 'info') return 'var(--blue)';
  return 'var(--text-ghost)';
}

/** Pace status → chip variant */
export function paceVariant(s: string) {
  if (s === 'on_track') return 'success' as const;
  if (s === 'off_track') return 'error' as const;
  if (s === 'at_risk') return 'warning' as const;
  if (s === 'not_measurable') return 'muted' as const;
  return 'muted' as const;
}

/** Webhook status → CSS color */
export function whColor(s: string): string {
  if (s === 'healthy') return 'var(--green)';
  if (s === 'warning') return 'var(--yellow)';
  return 'var(--text-ghost)';
}

/** Alert type → recommended action label */
export function actionLabel(alertType: string): string {
  if (alertType.includes('bounce')) return 'Investigate bounce rate, check list source';
  if (alertType.includes('domain')) return 'Review sending domains, pause if needed';
  if (alertType.includes('pacing')) return 'Increase volume or adjust targets';
  if (alertType.includes('stale')) return 'Follow up on stale interested leads';
  if (alertType.includes('reply')) return 'Review copy, audience, or sending patterns';
  return `Review: ${alertType.replace(/_/g, ' ')}`;
}

/** Metric column explanations — hover tooltips */
export const METRIC_TIPS: Record<string, string> = {
  sent: 'Total emails sent this period via PlusVibe',
  contacted: 'Unique contacts reached (deduplicated across sequences)',
  replied: 'Contacts who replied at least once',
  reply_rate: 'Replies ÷ Sent × 100. Sequence-level metric, not per-contact.',
  pv_positive: 'PlusVibe Positive — replies classified as interested by PlusVibe',
  missive_positive: 'Missive Positive — conversations labeled interested in Missive inbox',
  bounced: 'Hard + soft bounces. High rates indicate list quality issues.',
  booked_calls: 'Discovery/demo calls booked. Primary commercial KPI.',
  lead_velocity_weekly: 'New leads added per week. Proxy metric for pipeline health.',
  projected: 'Linear projection to month-end based on current pace.',
  required_per_week: 'Weekly rate needed to close the gap by month-end.',
  gap: 'Target minus current. Negative = ahead of pace.',
  confidence: 'How reliable the conversion rates are. Based on data volume.',
  shadow: 'Unresolved Missive conversations — client attribution uncertain.',
  proxy: 'Indirect indicator. Not the primary KPI but directionally useful.',
  is_proxy: 'This metric is a proxy — not the primary commercial target.',
  not_measurable: 'Cannot be measured from current data sources.',
};
